import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '@/components';
import { BREAKPOINTS, Container, Flex } from '@/ui/layout';
import { Body2Light, Text, Title } from '@/ui/typography';
import { EntitySection } from '@/modules/shared/components/EntitySection';
import Markdown from 'react-markdown';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import {
  SHOWED_SECTIONS_COUNT,
  STATUS_COLOR,
  STATUS_DISPLAY,
  STATUSES_WITH_ANSWERS,
  STATUSES_WITH_DISABLED
} from '@/utils/static/kyc';
import { kycHelpers } from '@/utils/helpers/kyc';
import { useTranslation } from 'react-i18next';
import { ArrowLeftOutlined, LinkOutlined } from '@ant-design/icons';
import { Divider, Form, Row, Col, Alert, Tag } from 'antd';
import { API_URL, COLORS } from '@/utils/static';
import { downloadFileByLink, getExtensionFromUrl, getLocalizedValue } from '@/utils/helpers';
import { usePopups, useEntityStatus } from '@/hooks';
import { PRODUCT_SPECIFIC_FIELDS } from '@/features/constructorProducts/components/productFieldsConfig';
import { CollapseList } from '@/components/common/molecules/CollapseList';

export const ProductView = ({
  product,
  currentSection,
  currentStep,
  onNext,
  onBack,
  onFinish,
  adminView = false,
  adminEditView = false,
  referralView = false,
  rejectProduct,
  acceptProduct,
  rejectedQuestions,
  setCurrentSection,
  setCurrentStep,
  disabled = false,
  stepsCount = 0,
  statusSettings,
  ...props
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id: investorId } = useParams();
  const [form] = Form.useForm();
  const { showErrorToast, showInfoToast } = usePopups();
  const [preFilledQuestions, setPreFilledQuestions] = useState({});
  const [creators, setCreators] = useState({});

  const currentLanguage = i18n.language;

  const { isInProcess, isRejected } = useEntityStatus(product);

  const defaultValues = useMemo(() => {
    try {
      if (product && (STATUSES_WITH_ANSWERS[product.status] || adminView)) {
        const { values, lastAnsweredSection, preFilledQuestions, creators } =
          kycHelpers.collectProductsAnswers(product);
        if (
          (!adminView && (isInProcess || isRejected)) ||
          (adminView && adminEditView && (isInProcess || isRejected))
        ) {
          setCurrentSection(Math.min(product.productSections.length - 1, lastAnsweredSection + 1));
          setCurrentStep(Math.min(product.productSections.length - 1, lastAnsweredSection + 1));
        }

        if (Object.keys(preFilledQuestions).length > 0) {
          setPreFilledQuestions(preFilledQuestions);
        }
        if (Object.keys(creators).length > 0) {
          setCreators(creators);
        }

        return values;
      }
      return null;
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
      return null;
    }
  }, [product, adminView]);

  const {
    control,
    handleSubmit,
    trigger,
    watch,
    formState: { errors, submitCount }
  } = useForm({ defaultValues: { ...defaultValues } });

  useEffect(() => {
    if (Object.keys(errors).length > 0)
      showInfoToast(
        t('Some fields are not valid. Please check the questions for any error texts.')
      );
  }, [submitCount]);

  const backToProfile = () => {
    adminView || adminEditView
      ? navigate(`/investor/${investorId}/products`)
      : navigate('/profile/products');
  };
  const startSlicePos = currentSection * SHOWED_SECTIONS_COUNT;

  const productParameters = useMemo(() => {
    const upperLang = currentLanguage.toUpperCase();
    const properties = [
      'interestRate',
      'interestPaid',
      'minimalAmount',
      'term',
      'redemption',
      'offerAndConditions',
      'offerPeriodUntil'
    ];

    return Object.fromEntries(
      Object.entries(product).filter(([key, value]) => {
        if (!value) return null;
        return properties.includes(`${key}_${upperLang}`) || properties.includes(key);
      })
    );
  }, [product, currentLanguage]);

  if (product) {
    return (
      <>
        <HeaderRow justify='space-between' align='center'>
          <Container $mdHidden>
            <Container $maxWidth={238} $wide>
              <Button
                type='text'
                size='large'
                text={t('Back to products')}
                icon={<ArrowLeftOutlined />}
                onClick={backToProfile}
              />
            </Container>
          </Container>

          <Container $mdVisible $hidden>
            <Button type='text' size='large' icon={<ArrowLeftOutlined />} onClick={backToProfile} />
          </Container>

          <Tag color={STATUS_COLOR[product?.status]} variant='filled'>
            {t(STATUS_DISPLAY[product?.status])}
          </Tag>
        </HeaderRow>

        <ProductInfoRow justify='center'>
          <ProductInfoRowInner align='flex-start' vertical gap={8}>
            {!disabled && (
              <Title level={2}>{getLocalizedValue(currentLanguage, 'name', product)}</Title>
            )}
            <Flex justify='space-between' gap={20}>
              {product?.company ? (
                <Text $fontSize={14}>
                  <Text $fontSize={14} $color={COLORS.greyText}>
                    {t('Issuer')}:{' '}
                  </Text>
                  <Text $fontSize={14} $fontWeight={600}>
                    {getLocalizedValue(currentLanguage, 'company', product) ?? '-'}
                  </Text>
                </Text>
              ) : (
                <></>
              )}
              {product?.investmentType ? (
                <Text $fontSize={14}>
                  <Text $fontSize={14} $color={COLORS.greyText}>
                    {t('Type')}:{' '}
                  </Text>
                  <Text $fontSize={14} $fontWeight={600}>
                    {getLocalizedValue(currentLanguage, 'investmentType', product) ?? '-'}
                  </Text>
                </Text>
              ) : (
                <></>
              )}
              {product?.isin ? (
                <Text $fontSize={14}>
                  <Text $fontSize={14} $color={COLORS.greyText}>
                    ISIN:{' '}
                  </Text>
                  <Text $fontSize={14} $fontWeight={600}>
                    {getLocalizedValue(currentLanguage, 'isin', product) ?? '-'}
                  </Text>
                </Text>
              ) : (
                <></>
              )}
              {product?.linkToCentralBankOfLatvia ? (
                <a
                  target='_blank'
                  href={product.linkToCentralBankOfLatvia}
                  rel='noreferrer'
                  style={{ fontSize: '14px' }}
                >
                  {t('Register of the Bank of Latvia')} ↗
                </a>
              ) : (
                <></>
              )}
            </Flex>
          </ProductInfoRowInner>
        </ProductInfoRow>

        {disabled && (
          <Title level={1} $textAlign={'center'} style={{ margin: '40px 0' }}>
            {t('Access denied')}
          </Title>
        )}

        <ProductContent justify='center'>
          <Row
            gutter={[32, 24]}
            justify='center'
            style={{ width: '100%', maxWidth: '1080px', marginTop: '32px', position: 'relative' }}
          >
            <Col xs={{ span: 24 }} md={{ span: 15 }}>
              <Flex vertical gap={24} justify='flex-start'>
                {product.logoURL && (
                  <Img
                    src={`${API_URL}/${product.logoURL}`}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                    alt=''
                  />
                )}
                <Box>
                  <Text strong $fontSize={18}>
                    {t('Product parameters')}
                  </Text>
                  <Container $marginTop={20}>
                    {Object.entries(productParameters).map(([key, value], index) => {
                      return (
                        <>
                          <Row
                            gutter={[16, 16]}
                            style={{
                              padding: '4px 0'
                            }}
                          >
                            <Col span={12} style={{ fontSize: '15px', color: COLORS.greyText }}>
                              {t(PRODUCT_SPECIFIC_FIELDS.find((obj) => obj.name === key)?.label)}
                            </Col>
                            <Col span={12} style={{ fontSize: '15px', fontWeight: 600 }}>
                              {value}
                            </Col>
                          </Row>
                          {index < Object.entries(productParameters).length - 1 && (
                            <Divider size='small' />
                          )}
                        </>
                      );
                    })}
                  </Container>
                </Box>

                <Box>
                  <Flex vertical>
                    <Text strong $fontSize={18}>
                      {t('About product')}
                    </Text>
                    <div
                      className='markdown'
                      style={{ fontSize: '16px', color: COLORS.greyText, marginTop: '6px' }}
                    >
                      <Markdown>
                        {getLocalizedValue(currentLanguage, 'descriptions', product)}
                      </Markdown>
                    </div>

                    <CollapseList
                      items={product.productDescriptions?.map((item) => ({
                        key: item.id,
                        label: getLocalizedValue(currentLanguage, 'name', item),
                        children: <p>{getLocalizedValue(currentLanguage, 'descriptions', item)}</p>
                      }))}
                    />
                  </Flex>
                </Box>

                {product.productFileToDownloads &&
                product.productFileToDownloads.length > 0 &&
                product.productLinks &&
                product.productLinks.length > 0 ? (
                  <Box>
                    <Text strong $fontSize={18}>
                      {t('Documents')}
                    </Text>

                    <Flex $marginTop={20} gap={8} vertical>
                      {product.productFileToDownloads.map((file) => {
                        return (
                          <DocumentLine justify='space-between' align='center' gap={20}>
                            <Flex gap={14} align='center'>
                              <DocumentExtensionIcon style={{ flexShrink: 0 }}>
                                {getExtensionFromUrl(file.fileURL)}
                              </DocumentExtensionIcon>
                              <Text $fontSize={15}>{file.name}</Text>
                            </Flex>
                            <Button
                              type='text'
                              block={false}
                              text='Download'
                              onClick={() => downloadFileByLink(file.name, file.fileURL)}
                              style={{ flexShrink: 0 }}
                            />
                          </DocumentLine>
                        );
                      })}

                      {product.productLinks.map((link) => {
                        return (
                          <DocumentLine justify='space-between' align='center' gap={20}>
                            <Flex gap={14} align='center'>
                              <DocumentExtensionIcon style={{ flexShrink: 0, fontSize: '13px' }}>
                                <LinkOutlined />
                              </DocumentExtensionIcon>
                              <Text $fontSize={15}>{link.name}</Text>
                            </Flex>
                            <Button
                              type='text'
                              block={false}
                              text='Open'
                              onClick={() => window.open(link.link)}
                              style={{ flexShrink: 0 }}
                            />
                          </DocumentLine>
                        );
                      })}
                    </Flex>
                  </Box>
                ) : null}
              </Flex>
            </Col>
            <Col
              xs={{ span: 24 }}
              md={{ span: 9 }}
              style={{ position: 'sticky', top: 28, zIndex: 1, alignSelf: 'start' }}
            >
              <Box>
                <Flex vertical>
                  <Flex gap={8} align='center'>
                    <Text strong $fontSize={26}>
                      {getLocalizedValue(currentLanguage, 'interestRate', product) ?? '-'}
                    </Text>
                    {/*<Text $fontSize={13} $color={COLORS.greyText}>*/}
                    {/*  годовых, фиксированная ставка*/}
                    {/*</Text>*/}
                  </Flex>
                  <Divider size='middle' />

                  <Flex vertical>
                    <Form
                      layout='vertical'
                      form={form}
                      name='form'
                      style={{ width: '100%', maxWidth: 600 }}
                    >
                      {!disabled && (
                        <>
                          {product.productSections && product.productSections.length > 0 ? (
                            product.productSections
                              .slice(startSlicePos, startSlicePos + SHOWED_SECTIONS_COUNT)
                              .map((sectionObj) => (
                                <div key={sectionObj.id}>
                                  <EntitySection
                                    entityType='product'
                                    productId={product?.id}
                                    productStatus={product?.status}
                                    productMinimalAmount={product?.minimalAmount}
                                    section={sectionObj}
                                    control={control}
                                    watch={watch}
                                    submitCount={submitCount}
                                    trigger={trigger}
                                    adminView={adminView}
                                    referralView={referralView}
                                    disableAll={
                                      adminView || STATUSES_WITH_DISABLED[product?.status]
                                    }
                                    rejectedQuestions={rejectedQuestions}
                                    preFilledQuestions={preFilledQuestions}
                                    creators={creators}
                                    {...props}
                                  />
                                </div>
                              ))
                          ) : (
                            <Flex $marginTop={60} justify='center'>
                              <Body2Light color={COLORS.greyMedium}>
                                {t(
                                  'Something went wrong. Please, reload the page or try again later.'
                                )}
                              </Body2Light>
                            </Flex>
                          )}
                        </>
                      )}
                    </Form>
                  </Flex>

                  {!disabled && product.productSections && !referralView && (
                    <Flex $marginTop={20} gap={10} vertical align='center'>
                      {!adminView && (
                        <Button
                          type='filled'
                          size='large'
                          text={t('Next')}
                          onClick={adminView ? onNext : () => handleSubmit(onNext)()}
                          disabled={disabled}
                        />
                      )}
                      {adminView && statusSettings && acceptProduct && (
                        <Button
                          type='filled'
                          size='large'
                          text={statusSettings.isApproved ? t('Approved') : t('Approve')}
                          onClick={acceptProduct}
                          disabled={!statusSettings.allowApprove}
                          $paddingX={'18'}
                        />
                      )}
                      {adminView && statusSettings && rejectProduct && (
                        <Button
                          size='large'
                          text={statusSettings.isRejected ? t('Rejected') : t('Reject')}
                          onClick={rejectProduct}
                          disabled={!statusSettings.allowReject}
                        />
                      )}
                    </Flex>
                  )}

                  <Container $marginTop={20}>
                    <Alert
                      message={
                        <span>
                          <Text $fontSize={13} strong>
                            {t('Adjustable tool')}.
                          </Text>{' '}
                          <Text $fontSize={13} $color={COLORS.greyText}>
                            {t('The prospectus has been approved by the Bank of Latvia')}. ISIN{' '}
                            {product?.isin}.
                          </Text>
                        </span>
                      }
                      type='info'
                      showIcon
                      icon={<span>🛡️</span>}
                    />
                  </Container>
                </Flex>
              </Box>
            </Col>
          </Row>
        </ProductContent>

        <FooterRow justify='center'>
          <FooterRowInner>
            <Text $fontSize={13} $color={COLORS.greyText}>
              {t(
                'Investing in bonds involves risk. Before making a decision, please read the prospectus and terms of the issue.'
              )}
            </Text>
          </FooterRowInner>
        </FooterRow>

        {/*{disabled && (*/}
        {/*  <Title level={1} $textAlign={'center'}>*/}
        {/*    {t('Access denied')}*/}
        {/*  </Title>*/}
        {/*)}*/}

        {/*{!disabled && product.productSections && !referralView && (*/}
        {/*  <Flex*/}
        {/*    $marginTop={20}*/}
        {/*    gap={20}*/}
        {/*    justify='flex-end'*/}
        {/*    wrap*/}
        {/*    align='center'*/}
        {/*    style={{*/}
        {/*      padding: '15px 105px 15px 16px',*/}
        {/*      background: '#f6f9ff',*/}
        {/*      boxShadow: '0px -3px 10px 0px rgba(0, 0, 0, 0.1)',*/}
        {/*      position: 'sticky',*/}
        {/*      bottom: 0*/}
        {/*    }}*/}
        {/*  >*/}
        {/*    {product?.status === 'UNACCEPTED' && (*/}
        {/*      <Container $minWidth={160}>*/}
        {/*        <Button*/}
        {/*          type='filled'*/}
        {/*          size='large'*/}
        {/*          text={t('Send on review')}*/}
        {/*          onClick={adminView ? onFinish : () => handleSubmit(onFinish)()}*/}
        {/*        />*/}
        {/*      </Container>*/}
        {/*    )}*/}
        {/*    <Container $minWidth={120}>*/}
        {/*      <Button*/}
        {/*        type='filled'*/}
        {/*        size='large'*/}
        {/*        text={t('Next')}*/}
        {/*        onClick={adminView ? onNext : () => handleSubmit(onNext)()}*/}
        {/*        disabled={disabled}*/}
        {/*      />*/}
        {/*    </Container>*/}
        {/*  </Flex>*/}
        {/*)}*/}
      </>
    );
  }
};

const HeaderRow = styled(Flex)`
  background: #f6f9ff;
  box-shadow: 0px 4px 14px 0px rgba(0, 0, 0, 0.1);
  padding: 15px 44px 15px 48px;

  @media (max-width: ${BREAKPOINTS.md}px) {
    padding: 12px 24px;
  }
`;

const ProductInfoRow = styled(Flex)`
  padding: 30px 0;
  background: ${COLORS.white};
  border-bottom: 1px solid ${COLORS.greyBasic};
  border-top: 1px solid ${COLORS.greyBasic};
`;

const ProductInfoRowInner = styled(Flex)`
  width: 100%;
  max-width: 1080px;
  padding: 0 16px;
`;

const ProductContent = styled(Flex)`
  width: 100%;
  padding-bottom: 80px;
`;

const Box = styled(Container)`
  border-radius: 10px;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.greyBasic};
  padding: 20px;
`;

const DocumentLine = styled(Flex)`
  padding: 12px 16px;
  border: 1px solid ${COLORS.greyBasic};
  border-radius: 8px;
`;

const Img = styled.img`
  width: 100%;
  border-radius: 10px;
`;

const FooterRow = styled(Flex)`
  padding: 20px 0;
  background: ${COLORS.white};
  border-bottom: 1px solid ${COLORS.greyBasic};
  border-top: 1px solid ${COLORS.greyBasic};
`;

const FooterRowInner = styled(Flex)`
  width: 100%;
  max-width: 1080px;
  padding: 0 16px;
`;

const DocumentExtensionIcon = styled.div`
  background: #e5f2fc;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 600;
  color: #1f6dba;
`;
